import React, { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { PublicLayout } from '../../design-system/components/layouts';
export default function Terms() {
  const { props } = usePage();
  const companyName = props.app_config?.company_name ?? 'Speakflow';
  const supportEmail = props.app_config?.support_email ?? 'team@speakflow.com';
  const productUrl = props.app_config?.product_url ?? 'https://www.speakflow.com';
  const [activeSection, setActiveSection] = useState('');
  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: (
        <>
          <p>
            By accessing or using the {companyName} Affiliate Program (the "Program"), you agree to
            be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you
            may not participate in the Program.
          </p>
          <p>
            These Terms supersede all prior agreements, representations, and understandings relating
            to the Affiliate Program, including arrangements made under any previous affiliate
            platform.
          </p>
          <p>
            We reserve the right to update these Terms at any time. Continued participation in the
            Program after changes are posted constitutes acceptance of the revised Terms.
          </p>
        </>
      ),
    },
    {
      id: 'eligibility',
      title: '2. Eligibility',
      content: (
        <>
          <p>To participate in the Program, you must:</p>
          <ul>
            <li>Be at least 18 years of age</li>
            <li>Have a valid PayPal account for commission payments</li>
            <li>Submit an application and be approved by {companyName}</li>
            <li>Comply with all applicable laws and regulations</li>
          </ul>
          <p>
            {companyName} reserves the right to accept or reject any application at its sole
            discretion.
          </p>
        </>
      ),
    },
    {
      id: 'program-terms',
      title: '3. Affiliate Program Terms',
      content: (
        <>
          <p>
            As an approved affiliate, you will receive a unique referral link and optional coupon
            code. You earn commissions when new customers subscribe to {companyName} through your
            referral link or coupon code.
          </p>
          <p>
            Commission rates, durations, payment schedules, and other program details are determined
            by the campaign you are currently assigned to. Your campaign assignment reflects your
            current terms and supersedes any rates or durations previously offered. Program details
            may change at any time with notice.
          </p>
        </>
      ),
    },
    {
      id: 'commissions',
      title: '4. Commissions and Payments',
      content: (
        <>
          <p>
            Commissions are calculated based on qualifying referrals as defined by your assigned
            campaign. Key terms:
          </p>
          <ul>
            <li>Commissions are subject to a hold period before becoming payable</li>
            <li>Payments are made via PayPal to the email address on file</li>
            <li>Minimum payout thresholds may apply based on campaign settings</li>
            <li>
              Commissions may be reversed if a referred customer cancels or requests a refund within
              the applicable period
            </li>
          </ul>
          <p>You are responsible for any taxes owed on commission earnings.</p>
        </>
      ),
    },
    {
      id: 'prohibited-activities',
      title: '5. Prohibited Activities',
      content: (
        <>
          <p>Affiliates must not engage in the following activities:</p>
          <ul>
            <li>Self-referrals or referring your own accounts</li>
            <li>Using misleading or deceptive advertising practices</li>
            <li>Bidding on {companyName} brand keywords in paid advertising</li>
            <li>Sending unsolicited emails (spam) to promote referral links</li>
            <li>Using cookie stuffing, ad injection, or other fraudulent techniques</li>
            <li>Misrepresenting your relationship with {companyName}</li>
            <li>Violating any applicable laws, regulations, or third-party terms of service</li>
          </ul>
          <p>
            Violation of these terms may result in immediate termination from the Program and
            forfeiture of unpaid commissions.
          </p>
        </>
      ),
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property',
      content: (
        <>
          <p>
            {companyName} grants you a limited, non-exclusive, revocable license to use our brand
            name, logos, and marketing materials solely for the purpose of promoting {companyName}{' '}
            as part of the Program.
          </p>
          <p>
            You may not modify our trademarks or use them in a way that could cause confusion, imply
            endorsement, or damage our reputation.
          </p>
        </>
      ),
    },
    {
      id: 'termination',
      title: '7. Termination',
      content: (
        <>
          <p>
            Either party may terminate participation in the Program at any time.
            {companyName} may terminate your account immediately if you violate these Terms.
          </p>
          <p>Upon termination:</p>
          <ul>
            <li>All earned and payable commissions will be paid within 30 days</li>
            <li>
              Pending commissions within the hold period may be forfeited if termination is due to a
              Terms violation
            </li>
            <li>You must cease using all {companyName} branding and marketing materials</li>
            <li>Your referral links will be deactivated</li>
          </ul>
        </>
      ),
    },
    {
      id: 'limitation-of-liability',
      title: '8. Limitation of Liability',
      content: (
        <>
          <p>
            The Program is provided "as is" without warranties of any kind. {companyName} shall not
            be liable for any indirect, incidental, special, consequential, or punitive damages
            arising from your participation in the Program.
          </p>
          <p>
            Our total liability for any claims arising from the Program shall not exceed the total
            commissions paid to you in the 12 months preceding the claim.
          </p>
        </>
      ),
    },
    {
      id: 'modifications',
      title: '9. Modifications to the Program',
      content: (
        <p>
          {companyName} reserves the right to modify, suspend, or discontinue the Program (or any
          part thereof) at any time. We will provide reasonable notice of material changes when
          possible. Continued participation after changes take effect constitutes acceptance.
        </p>
      ),
    },
    {
      id: 'contact',
      title: '10. Contact Information',
      content: (
        <p>
          If you have questions about these Terms, please contact us at{' '}
          <a
            href={`mailto:${supportEmail}`}
            className="text-fg hover:text-fg-secondary underline"
          >
            {supportEmail}
          </a>
          .
        </p>
      ),
    },
  ];
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      },
    );
    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl font-semibold text-fg tracking-tight">
            Terms of Service
          </h1>
          <p className="mt-2 text-sm text-fg-tertiary">Last updated: February 2026</p>
        </div>

        <div className="flex gap-12">
          {/* Table of contents sidebar */}
          <nav className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-8">
              <h2 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wider mb-3">
                On this page
              </h2>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={`block text-sm py-1 transition-colors ${activeSection === section.id ? 'text-fg font-medium' : 'text-fg-tertiary hover:text-fg'}`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="prose prose-neutral max-w-none">
              <p className="text-fg-secondary leading-relaxed">
                These Terms of Service govern your participation in the {companyName} Affiliate
                Program. By joining the Program, you agree to comply with and be bound by these
                terms.
              </p>

              {sections.map((section) => (
                <section key={section.id} id={section.id} className="mt-10">
                  <h2 className="text-lg font-semibold text-fg mb-4">{section.title}</h2>
                  <div className="text-fg-secondary leading-relaxed space-y-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
                    {section.content}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
